import React from 'react';
import { InfoCard } from '../../../components/apidocs';
import type { EndpointSpec, Translate } from '../../../components/apidocs';

/** การ์ด 3 ตัวเลือกของการส่งสลิป (QR string / base64 / file) */
const SlipOptionCards: React.FC<{ t: Translate }> = ({ t }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
    {[
      { label: 'QR String', desc: t('apiDocs.slipOptQrDesc'), color: 'border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20', badge: 'bg-sky-100 text-sky-700' },
      { label: 'Base64 Image', desc: t('apiDocs.slipOptBase64Desc'), color: 'border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20', badge: 'bg-violet-100 text-violet-700' },
      { label: 'File Upload', desc: t('apiDocs.slipOptFileDesc'), color: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20', badge: 'bg-emerald-100 text-emerald-700' },
    ].map((opt, i) => (
      <div key={i} className={`rounded-xl border p-3 ${opt.color}`}>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${opt.badge}`}>{opt.label}</span>
        <p className="text-xs text-gray-600 dark:text-slate-300 mt-2">{opt.desc}</p>
      </div>
    ))}
  </div>
);

/** Slip Order API — ลูกค้าโอนแล้วส่งสลิปมายืนยัน */
export const SLIP_ORDER_ENDPOINTS = (t: Translate): EndpointSpec[] => [
  {
    method: 'POST',
    path: '/slip-orders',
    description: t('apiDocs.createSlipOrderDesc'),
    requests: [{
      heading: 'Request Body',
      variant: 'required',
      rows: [
        { name: 'amount', type: 'number', required: true, description: t('apiDocs.slipFieldAmount') },
        { name: 'bank_name', type: 'string', required: true, description: t('apiDocs.slipFieldBankName') },
        { name: 'bank_number', type: 'string', required: true, description: t('apiDocs.slipFieldBankNumber') },
        { name: 'reference_id', type: 'string', required: true, description: t('apiDocs.slipFieldReferenceId') },
        { name: 'customer_name', type: 'string', description: t('apiDocs.slipFieldCustomerName') },
        { name: 'customer_email', type: 'string', description: t('apiDocs.slipFieldCustomerEmail') },
        { name: 'customer_phone', type: 'string', description: t('apiDocs.slipFieldCustomerPhone') },
        { name: 'callback_url', type: 'string', description: t('apiDocs.slipFieldCallbackUrl') },
        { name: 'redirect_url', type: 'string', description: t('apiDocs.slipFieldRedirectUrl') },
      ],
    }],
    response: {
      status: 201,
      id: 'create-slip-order-res',
      code: `{
  "success": true,
  "message": "สร้าง Order สำเร็จ",
  "data": {
    "order_number": "ORD_abc123xyz",
    "amount": 500.00,
    "total_amount": 500.00,
    "slip_only": true,
    "bank_info": {
      "bank_name": "ธนาคารไทยพาณิชย์",
      "bank_code": "014",
      "account_number": "123-456789-0"
    },
    "status": "pending",
    "expires_at": "2025-10-09T12:40:29.000Z",
    "expires_in_seconds": 600,
    "redirect_url": "https://your-site.com/thank-you"
    "payment_url": "https://123pay.asia/pay/ORD_abc123xyz",
  }
}`,
    },
    footer: (
      <InfoCard type="tip" title={t('apiDocs.slipOrderNoteTitle')}>
        <code className="font-mono text-xs bg-amber-50 dark:bg-amber-900/30 px-1 rounded">bank_info</code>{' '}
        {t('apiDocs.slipOrderNoteContent')}
      </InfoCard>
    ),
  },
  {
    method: 'POST',
    path: '/orders/:order_number/verify-slip',
    description: t('apiDocs.verifySlipDesc'),
    intro: <SlipOptionCards t={t} />,
    requests: [
      {
        heading: 'Request — Option 1 & 2: JSON Body',
        note: (
          <p className="text-xs text-amber-600 mb-3">
            {t('apiDocs.slipAtLeastOne')} <code className="font-mono bg-amber-50 dark:bg-amber-900/30 px-1 rounded">qr_string</code> &gt;{' '}
            <code className="font-mono bg-amber-50 dark:bg-amber-900/30 px-1 rounded">imageBase64</code> &gt;{' '}
            <code className="font-mono bg-amber-50 dark:bg-amber-900/30 px-1 rounded">file</code>
          </p>
        ),
        rows: [
          { name: 'qr_string', type: 'string', description: t('apiDocs.slipQrStringDesc') },
          { name: 'imageBase64', type: 'string', description: t('apiDocs.slipImageBase64Desc') },
        ],
      },
      {
        heading: 'Request — Option 3: File Upload (multipart/form-data)',
        rows: [
          { name: 'file', type: 'file', description: t('apiDocs.slipFileDesc') },
        ],
      },
    ],
    response: {
      status: 200,
      id: 'verify-slip-res',
      code: `{
  "success": true,
  "message": "ตรวจสอบสลิปสำเร็จ ชำระเงินเรียบร้อย",
  "data": {
    "order_number": "ORD_abc123xyz",
    "amount": 500.00,
    "total_amount": 500.00,
    "status": "paid",
    "paid_at": "2025-10-09T12:38:00.000Z",
    "fee_amount": 10.00,
    "net_amount": 490.00
  }
}`,
    },
    errors: {
      header: t('apiDocs.slipErrorsHeader'),
      rows: [
        { http: '400', description: t('apiDocs.slipError400Duplicate') },
        { http: '400', description: t('apiDocs.slipError400Amount') },
        { http: '400', description: t('apiDocs.slipError400Account') },
        { http: '400', description: t('apiDocs.slipError400Sender') },
        { http: '400', description: t('apiDocs.slipError400Old') },
        { http: '404', description: t('apiDocs.slipError404') },
      ],
    },
  },
  {
    method: 'GET',
    path: '/orders/:order_number',
    description: t('apiDocs.checkOrderStatus'),
    response: {
      status: 200,
      id: 'slip-get-order-res',
      code: `{
  "success": true,
  "data": {
    "order_number": "ORD_abc123xyz",
    "reference_id": "YOUR-ORDER-123",
    "amount": 500.00,
    "total_amount": 500.00,
    "status": "paid",
    "created_at": "2025-10-09T12:30:29.000Z",
    "expires_at": "2025-10-09T12:40:29.000Z",
    "paid_at": "2025-10-09T12:38:00.000Z",
    "seconds_left": 0,
    "is_expired": false
  }
}`,
    },
    statuses: ['pending', 'paid', 'expired', 'cancelled'],
  },
];
